import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface IUpdateSendEmailFeaturesRequest {
	accountId: string;
	sendEmailFeatures: Partial<Api.ISendEmailFeatures>;
}

/**
 * Persists an account's send-email features (business-hours send intervals + holiday pausing)
 * — the react-query replacement for `AccountAdminViewModel.updateSendEmailFeatures`.
 * `PUT impersonate/{accountId}/Account/features/sendEmail` with the (merged) features object;
 * returns the saved `ISendEmailFeatures`.
 *
 * There is no global `MutationCache`, so callers MUST pass an explicit `onError` (e.g. a toast)
 * — errors are otherwise surfaced only via the rejected `mutateAsync` promise.
 */
export function useUpdateSendEmailFeaturesMutation(options?: {
	onError?: (error: unknown) => void;
	onSuccess?: (data: Api.ISendEmailFeatures) => void;
}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({ accountId, sendEmailFeatures }: IUpdateSendEmailFeaturesRequest) =>
			userSession.webServiceHelper.callAsync<Api.ISendEmailFeatures>(
				`impersonate/${accountId}/Account/features/sendEmail`,
				'PUT',
				sendEmailFeatures
			),
		onError: options?.onError,
		onSuccess: options?.onSuccess,
	});
}
